import React from 'react';
import styled from 'styled-components';
import { LabelItem } from './LabelItem';
import { UncontrolledTooltip } from 'reactstrap';
import { CFormSwitch } from '@coreui/react';
import theme from '../../assets/css/theme';
import { FormTooltip } from './InputTooltip';
import classNames from 'classnames';
import * as Popper from 'popper.js';
import { useNewDesign } from '../../hooks/useNewDesign';

interface ISwitcher {
  disabled: boolean;
  margin?: string;
  outline?: boolean;
  $newSwitch?: boolean;
  $redesign?: boolean;
}

const Switcher = styled(CFormSwitch)<ISwitcher>`
  margin: ${(props) => (props.margin || '0 10px 0 0') + '!important'};
  min-height: ${(props) => (props.$redesign ? '16px' : '18px')};

  .form-check-input {
    width: 27px;
    height: 16px;
    // !important is needed to beat the .form-switch .form-check-input rule in
    // scss/custom/_coreui-migration.scss, which otherwise forces 27x18 !important.
    ${(props) =>
      props.$redesign &&
      `width: 29px !important;
      height: 16px !important;`}
    margin-top: 0;
    background-color: ${(props) => (props.outline ? 'transparent' : '#7b7b7b')};
    border-color: #7b7b7b;
    cursor: pointer;
    ${(props) =>
      props.$newSwitch
        ? "background-image: url(\"data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='-4 -4 8 8'%3e%3ccircle r='3' fill='%23999'/%3e%3c/svg%3e\");"
        : ''}

    &:checked {
      background-color: ${(props) => (props.outline ? 'transparent' : theme.commonColors.second)};
      border-color: ${theme.commonColors.second};
      ${(props) =>
        props.$newSwitch
          ? "background-image: url(\"data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='-4 -4 8 8'%3e%3ccircle r='3' fill='%235D8762'/%3e%3c/svg%3e\");"
          : ''}
    }

    &:focus {
      box-shadow: none;
    }
  }
`;

interface IWrapper {
  direction: string;
  margin?: string;
}

const Wrapper = styled.div<IWrapper>`
  display: flex;
  flex-direction: ${(props) => props.direction};
  align-items: center;
  margin: ${(props) => props.margin || '0 0 20px 0'};
  justify-content: flex-end;
`;

interface IProps {
  value: boolean | number;
  withTooltip?: boolean;
  label?: any;
  id?: string;
  direction?: string;
  tooltipPlacement?: Popper.Placement;
  isIconTooltip?: boolean;
  tooltipText?: string;
  changeCallback: (value: boolean) => void;
  onBlurCallback?: () => void;
  margin?: string;
  isDisabled?: boolean;
  labelMargin?: string;
  switcherMargin?: string;
  outline?: boolean;
  newSwitch?: boolean;
}

// tslint:disable-next-line:cyclomatic-complexity
export const SwitchItem = (props: IProps) => {
  const {
    value,
    withTooltip,
    label,
    id,
    direction = 'row',
    tooltipPlacement = 'bottom',
    tooltipText,
    changeCallback,
    margin,
    isDisabled = false,
    isIconTooltip,
    labelMargin,
    switcherMargin,
    outline,
    newSwitch
  } = props;
  // Read directly rather than threading a prop through all ~28 call sites.
  const isNewDesign = useNewDesign();
  const tooltipID = `${id}_tooltip`;
  const tooltip =
    !isIconTooltip && withTooltip && id ? (
      <UncontrolledTooltip autohide={false} placement={tooltipPlacement} target={tooltipID}>
        {tooltipText}
      </UncontrolledTooltip>
    ) : null;
  const tooltipProps = {
    isShow: Boolean(withTooltip),
    tooltipID,
    formTheme: 'main' as const,
    tooltipPlacement,
    tooltipText
  };
  const labelCursorType = !isIconTooltip && withTooltip ? 'help' : undefined;
  const labelID = !isIconTooltip ? tooltipID : '';
  const showLabel = label ? (
    <LabelItem
      htmlFor={id}
      id={labelID}
      isSmall
      isBold={false}
      margin={direction === 'row' ? `0 ${labelMargin} 0 0` : '0'}
      cursor={labelCursorType}
    >
      {label}
      {isIconTooltip ? <FormTooltip {...tooltipProps} /> : null}
    </LabelItem>
  ) : null;
  const changeHandler = (e: React.BaseSyntheticEvent) => changeCallback(e.currentTarget.checked);

  return (
    <Wrapper direction={direction} margin={margin}>
      {tooltip}
      {showLabel}
      <Switcher
        // @ts-ignore reason: Unknown Heroku issue
        disabled={isDisabled}
        variant="pill"
        onChange={changeHandler}
        checked={Boolean(value)}
        margin={switcherMargin}
        data-cy="switcher"
        outline={outline}
        $newSwitch={newSwitch}
        $redesign={isNewDesign}
        className={classNames({ outline })}
      />
    </Wrapper>
  );
};

export default SwitchItem;
